// Tuiles et sections masquées (P12, démo de Besançon du 09/10, D11) — les règles pures, à part
// pour être testées sans rendre une page. Le stockage vit dans `prefs.ts` (par compte et par
// commune, comme les épingles) ; ici seulement « qu'est-ce qui est masqué dans CE payload ».
// Masquer n'efface rien : la donnée reste servie, en comparaison, en recherche et dans l'export
// intégral ; seules la vue de l'utilisateur, son étude PDF et l'export de la thématique l'omettent.
import { memeMasque, type Masque } from './prefs'
import type { Section } from './types'

/** Le contexte d'une section tel que l'écran le passe aux tuiles (`PinCtx` de components.tsx),
    réduit à ce qu'il faut ici — sans importer components.tsx, qui importe ce module. */
interface CtxMasque {
  t: string
  s: string
  api: { estMasque?: (m: Masque) => boolean }
}

/** Cet élément est-il masqué par le compte ? `k` absent : la section entière. */
export function estMasqueCtx(ctx: CtxMasque | undefined, k?: string, c?: boolean): boolean {
  if (!ctx?.api.estMasque) return false
  return ctx.api.estMasque(k === undefined ? { t: ctx.t, s: ctx.s } : { t: ctx.t, s: ctx.s, k, c: !!c })
}

export type EstMasque = (m: Masque) => boolean

export const sectionMasquee = (estMasque: EstMasque, t: string, s: string) => estMasque({ t, s })
export const tuileMasquee = (estMasque: EstMasque, t: string, s: string, k: string, c = false) =>
  estMasque({ t, s, k, c })

/** Le compteur « Afficher les éléments masqués (n) » : ce qui est masqué ET présent dans la page.
    Un masque dont la tuile a disparu (libellé renommé, section retirée) n'est pas compté — sinon
    le bouton promettrait un élément que rien ne réaffiche. Une tuile d'une section masquée
    compte avec sa section, pas en plus. */
export function compterMasques(sections: Section[], t: string, estMasque: EstMasque): number {
  let n = 0
  for (const s of sections) {
    if (sectionMasquee(estMasque, t, s.id)) { n += 1; continue }
    n += (s.kpis ?? []).filter(k => !k.gated && tuileMasquee(estMasque, t, s.id, k.label)).length
    n += (s.charts ?? []).filter(c => !!c.title && tuileMasquee(estMasque, t, s.id, c.title, true)).length
  }
  return n
}

/** Les sections d'une thématique sans ce qui est masqué — pour l'étude PDF (aucun bouton n'y
    réaffiche quoi que ce soit). Une copie : le payload reçu n'est jamais modifié. */
export function sansMasques(sections: Section[], t: string, masques: Masque[]): Section[] {
  if (!masques.some(m => m.t === t)) return sections
  const est: EstMasque = m => masques.some(x => memeMasque(x, m))
  return sections.filter(s => !sectionMasquee(est, t, s.id)).map(s => ({
    ...s,
    kpis: s.kpis?.filter(k => !tuileMasquee(est, t, s.id, k.label)),
    charts: s.charts?.filter(c => !c.title || !tuileMasquee(est, t, s.id, c.title, true)),
  }))
}
